<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { type IconLease, isVectorIcon, leaseIcon, type ResolvedIcon } from "#ui/utils/icon";

export interface Props {
	src?: string | null;
	color?: string | null;
	size?: number;
	vector?: boolean;
	reserveSpace?: boolean;
}

const props = withDefaults(defineProps<Props>(), { size: 30 });

const emit = defineEmits<{ error: [] }>();

const failed = ref(false);
const resolved = ref<ResolvedIcon | null>(null);

let lease: IconLease | null = null;

const isLocal = (source?: string | null) => typeof source === "string" && /^(blob|data):/.test(source);

const source = computed(() => (resolved.value ? resolved.value.url : props.src || null));
const isVector = computed(() => resolved.value?.vector ?? props.vector ?? isVectorIcon(props.src));
const isVisible = computed(() => !!source.value && !failed.value);
const isMaskable = computed(() => isLocal(source.value));
const isTinted = computed(() => isVisible.value && isVector.value && isMaskable.value && !!props.color);

const boxStyle = computed(() => ({ width: `${props.size}px`, height: `${props.size}px` }));
const maskStyle = computed(() => {
	const image = `url(${JSON.stringify(source.value)})`;
	return { ...boxStyle.value, backgroundColor: props.color as string, maskImage: image, WebkitMaskImage: image };
});

const fail = () => {
	failed.value = true;
	emit("error");
};

const releaseLease = () => {
	lease?.release();
	lease = null;
};

watch(
	() => props.src,
	async (src) => {
		failed.value = false;
		resolved.value = null;
		releaseLease();
		if (!src || isLocal(src)) return;

		const current = leaseIcon(src);
		lease = current;
		const icon = await current.icon;
		if (lease !== current) return;

		resolved.value = icon;
		if (!icon.url) fail();
	},
	{ immediate: true },
);

onBeforeUnmount(releaseLease);
</script>

<template>
	<span
		v-if="isVisible || reserveSpace"
		class="tinted_icon tw:inline-flex tw:flex-none tw:items-center tw:justify-center"
		:class="{ 'tinted_icon_mask tw:mask-contain tw:mask-center tw:mask-no-repeat': isTinted }"
		:style="isTinted ? maskStyle : boxStyle"
	>
		<img v-if="isVisible && !isTinted" class="tw:h-full tw:w-full tw:object-contain" :src="source || ''" alt="" @error="fail">
	</span>
</template>
